'use strict';

export class AssetsLoader {
	
	load_assets_only(result) {
		if (result.js_variables?.components) {
			yalwa.v.components = {...yalwa.v.components, ...result.js_variables.components};
		}
		this.init_components();
	}
	
	/**
	 * Loads CSS and JavaScript assets from the provided result object and appends the HTML content to the body.
	 * @param {Object} result - The result object containing 'assets' and 'html' properties.
	 * @param {Object} result.$append_to - container for appending the html content.
	 * @param {Object} result.assets - An object containing arrays of CSS and JavaScript URLs.
	 * @param {Array<string>} result.assets.css - An array of CSS file URLs.
	 * @param {Array<string>} result.assets.js - An array of JavaScript file URLs.
	 * @param {string} result.html - The HTML content to be appended to the body.
	 */
	load_assets_and_append_html(result) {
		if (result.js_variables?.components) {
			yalwa.v.components = {...yalwa.v.components, ...result.js_variables.components};
		}
		// Extract assets and HTML content from the result object
		const { assets, html } = result;
		// Load CSS files
		const css_promises = (assets.css || []).map(url => yalwa.basics.load_css(url));
		// Load JS files
		const js_promises = (assets.js || []).map(url => yalwa.basics.load_js(url, null, 'module'));
		// Wait for all assets to load
		return Promise.all([...css_promises, ...js_promises])
			.then(() => {
				if (result.$append_to) {
					// Append HTML content to given container
					result.$append_to.append(result.html);
				}
				if (result.$before) {
					// Append HTML content before given container
					result.$before.before(result.html);
				}
				return new Promise(resolve => requestAnimationFrame(resolve));
			})
			.then(() => this.init_components())
			.then(() => this.init_vue_components())
			.catch(error => {
				console.error('Failed to load assets:', error);
			});
	}
	vue_components = [];
	dont_init = ['dialog_phone', 'dialog_aboutme', 'dialog_website'];
	async init_components() {
		yalwa.v.component_instances = yalwa.v.component_instances || {};
		if (yalwa.v.components_nocache) {
			yalwa.v.components = {...yalwa.v.components, ...yalwa.v.components_nocache};
		}
		if (!yalwa.v.components) {
			return;
		}
		// TODO use this for all components
		const promises = Object.keys(yalwa.v.components).map(async component_key => {
		const component = yalwa.v.components[component_key];
		// Skip anything already running. Dialogs are registered by dialog.js in
		// components.initialized, everything else lands in component_instances, so both
		// have to be checked: a second instance would hold no bound events and would
		// replace the live one in component_instances.
		if (yalwa.v.component_instances[component_key]
			|| (yalwa.v.components.initialized || {})[component_key]) {
			return;
		}
		if (component.use_global_js) {
			if (this.dont_init.includes(component_key)) {
				return;
			}
			try {
				
				let class_name, file_name;

				class_name = yalwa.basics.to_pascal_case(component.component_type);
				file_name = component.component_type;
				
				// import (browser ESM cache deduplicates repeated imports of the same module)
				const module = await import(`./${component.component_type}/${file_name}.js`);
				// instantiate and initialize
				const component_class = module[class_name];
				const yc = new component_class(component_key);
				yalwa.v.component_instances[component_key] = yc;
				yc.init();
				if (typeof yc.vue_setup === 'function') {
					this.vue_components.push(yc);
				}
			} catch (error) {
				console.error(`Failed to load or initialize class for ${component_key}:`, error);
			}
		} else if ( // intalize for each instance specific module
			component_key.startsWith('dropdown_') ||
			component_key.startsWith('dialog_') ||
			component_key.startsWith('banner_') ||
			component_key.startsWith('collapsebox_') ||
			component_key.startsWith('safetytips_') ||
			component_key.startsWith('actions_') ||
			component_key.startsWith('button_') ||
			component_key.startsWith('tabs_') ||
			component_key.startsWith('infoblock_') ||
			component_key.startsWith('gallery_') ||
			component_key.startsWith('promoslider_') ||
			component_key.startsWith('molecule_') ||
			component_key.startsWith('faq_search_') ||
			component_key.startsWith('organism_') ||
			component_key.startsWith('accordion_') ||
			component_key.startsWith('transcriber_')  ||
			component_key.startsWith('menuhorizontal_') ||
			component_key.startsWith('iospicker_sneakpiciospicker')
		) {
				if (this.dont_init.includes(component_key)) {
					return;
				}
				try {
					
					let class_name, file_name;
					if (component.js_group_name) {
						class_name = yalwa.basics.to_pascal_case(component.js_group_name);
						file_name = component.js_group_name;
					} else {
						class_name = yalwa.basics.to_pascal_case(component_key);
						file_name = component_key;
					}
					// import (browser ESM cache deduplicates repeated imports of the same module)
					const module = await import(`./${component.component_type}/${file_name}.js`);
					// instantiate and initialize
					const component_class = module[class_name];
					const yc = new component_class(component_key);
					yalwa.v.component_instances[component_key] = yc;
					yc.init();
					if (typeof yc.vue_setup === 'function') {
						this.vue_components.push(yc);
					}
					if (
						!component_key.startsWith('dialog_') &&
						!component_key.startsWith('dropdown_') &&
						!component_key.startsWith('infoblock_') &&
						!component_key.startsWith('tabs_') &&
						!component_key.startsWith('gallery_')
					) {
						delete yalwa.v.components[component_key]
					}
				} catch (error) {
					console.error(`Failed to load or initialize class for ${component_key}:`, error);
				}
			}
		})
		// wait for all async imports/initializations
		await Promise.all(promises);
	}
	Vue_app_initialized = false;
	async init_vue_components() {
		if (!this.vue_components?.length) {
			return;
		}
		if (this.Vue_app_initialized) {
			return;
		}
		const Vue = await import(`../external/vue/vue.esm-browser.prod.js`); // for debugging replace .prod with .min
		// const Vue = await import(`https://cdnjs.cloudflare.com/ajax/libs/vue/3.5.20/vue.esm-browser.min.js`); // for debugging replace .prod with .min

		// hidden dummy mount element
		const dummy = document.createElement('div');
		dummy.style.display = 'none';
		document.body.appendChild(dummy);
		
		// reactive list
		this.reactive_vue_components = Vue.reactive(this.vue_components);
		
		// --- keep track of initialized component IDs ---
		const initialized = new Set();
		
		const App = {
			props: ['vue_components'],
			setup(props) {
				const placeholders = Vue.ref([]);
				const components = Vue.shallowRef({});
				const cleanupSSR = el => {
					return;
					while (el.firstChild && !el.firstChild.__vnode) {
						el.removeChild(el.firstChild);
					}
				};
				
				const rebuild = () => {
					const defs = props.vue_components
						.filter(c => typeof c.vue_setup === 'function' && !initialized.has(c.id))
						.map(c => {
							initialized.add(c.id);
							return { key: c.id, comp: c.vue_setup(Vue) };
						});
					
					if (!defs.length) return;
					
					// merge into component map
					const comps = { ...components.value };
					defs.forEach(c => {
						comps[c.key] = Vue.markRaw(c.comp);
					});
					
					// collect placeholders
					const phs = [...placeholders.value];
					defs.forEach(c => {
						const els = Array.from(document.querySelectorAll(`.js-${c.key}`));
						els.forEach(el => {
							// --- clean SSR children before teleport happens ---
							while (el.firstChild && !el.firstChild.__vnode) {
								el.removeChild(el.firstChild);
							}
							phs.push({ key: c.key, el });
						});
					});
					
					components.value = comps;
					placeholders.value = phs;
				};
				
				// initial build
				rebuild();
				
				// watch for pushes into vue_components
				Vue.watch(
					() => props.vue_components.length,
					() => rebuild()
				);
				
				return { placeholders, components, cleanupSSR };
			},
			template: `
		<div>
			<template v-for="p in placeholders">
				<Teleport :to="p.el">
					<component :is="components[p.key]" />
				</Teleport>
			</template>
		</div>
    `
		};
		
		Vue.createSSRApp(App, { vue_components: this.reactive_vue_components }).mount(dummy);
		
		this.Vue_app_initialized = true;
		//setTimeout(() => this.test_ajax(), 1);
	}
	async test_ajax() {
		// test: dynamically add one later
		const component = yalwa.v.components.breadcrumb_vap;
		const class_name = yalwa.basics.to_pascal_case('breadcrumb');
		const module = await import(`./${component.component_type}/breadcrumb.js`);
		const component_class = module[class_name];
		const comp = new component_class('breadcrumb_vap');
		await comp.init();
		
		if (typeof comp.vue_setup === 'function') {
			this.reactive_vue_components.push(comp);
		}
	}
	
	async init_component_by_key(component_key) {
			const component = yalwa.v.components[component_key];
			try {
				if (component.js_group_name) {
					// instantiate and initialize
					const class_name = yalwa.basics.to_pascal_case(component.js_group_name);
					const module = await import(`./${component.component_type}/${component.js_group_name}.js`);
					const component_class = module[class_name];
					new component_class(component_key).init();
					return;
				}
				
				const class_name = yalwa.basics.to_pascal_case(component_key);
				// import
				const module = await import(`./${component.component_type}/${component_key}.js`);
				// instantiate and initialize
				const component_class = module[class_name];
				const yc = new component_class(component_key);
				yalwa.v.component_instances[component_key] = yc;
				yc.init();
				if (typeof yc.vue_setup === 'function') {
					this.vue_components.push(yc);
				}
				if (
					!component_key.startsWith('dialog_') &&
					!component_key.startsWith('dropdown_') &&
					!component_key.startsWith('infoblock_') &&
					!component_key.startsWith('gallery_')
				) {
					delete yalwa.v.components[component_key]
				}
			} catch (error) {
				console.error(`Failed to load or initialize class for ${component_key}:`, error);
			}
		}
}

